// deps
import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo } from "react";

// internal-deps
import {
    GlobalHeaderState,
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    useNavigate,
    AnalyticsContext,
    recordNREvent,
    closeWebview,
    getOriginalBusinessLine,
    NEWRELIC_TABLES,
    useCustomHardwareBack,
} from "@swiggy-private/checkout-core";
import { GenericError } from "@swiggy-private/core-ui";
// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";

import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import { AddSwiggyMoney, SwiggyMoneyLoadingScreen } from "@swiggy-private/payment-ui";
// constants
import { TEXTS } from "@payment-client/constants/SwiggyMoney";

// utils
import {
    getAdditionalRechargeRequired,
    getAddSwiggyMoneySubtitle,
    getRechargeAmountSelectionType,
} from "@payment-client/helpers/SwiggyInstrumentsHelper";
import { isOriginalBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";

// contexts
import { AddSwiggyMoneyContext } from "@payment-client/contexts/AddSwiggyMoneyContext";

// events
import {
    clickPredefinedRechargeAmount,
    clickRedeemVoucherSwMoney,
    onAddSwiggyMoneyScreenView,
    onProceedWithRecharge,
} from "@payment-client/analytics/events/AddSwiggyMoney";

import {
    clickSwiggyMoneyInfoCta,
    closeSwiggyMoneyInfoHalfCard,
    impressionSwiggyMoneyInfoHalfCard,
    impressionSwiggyMoneyInfoCta,
} from "@payment-client/analytics/events/SwiggyMoneyInfoHalfCard";
import { CONTEXT, SCREEN_NAME } from "@payment-client/analytics/constants";

import styles from "./styles.v4.scss";

type AddSwiggyMoneyProps = RouteProps<{
    billTotal?: number;
}>;

const ADD_VOUCHER_CLICK = "ADD_VOUCHER_CLICK";

export const AddSwiggyMoneyContainer = memo(function AddSwiggyMoneyContainer(
    props: AddSwiggyMoneyProps,
): ReactElement | null {
    const { data, getAddMoneyRequestData, updateSelectedAmount, selectedAmount, originalBillAmount } = useContext(
        AddSwiggyMoneyContext,
    );
    const eligibleBalance = useMemo(() => data.addMoneyData?.eligibleBalance, [data.addMoneyData]);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const navigateTo = useNavigate();
    const isSwMoneyBusinessLine = useMemo(() => isOriginalBusinessLineSwiggyMoney(), []);

    const requestAddMoneyData = useCallback(() => {
        getAddMoneyRequestData(props.data?.billTotal);
    }, [getAddMoneyRequestData, props.data]);

    const onAddMoney = useCallback(() => {
        // need amount in headers, as getPaymentOptions api will use this value
        GlobalHeaderState.updateHeaders({
            paymentamount: String(selectedAmount),
        });
        navigateTo(CheckoutRoute.SWIGGY_MONEY_PAYMENTS);

        // need to resolve type of selection, as it is needed for ARD
        const amountSuggestions = data.addMoneyData?.eligibilityDetails?.suggestedAmounts;
        const selectionType = getRechargeAmountSelectionType(selectedAmount, amountSuggestions);

        onProceedWithRecharge(String(selectedAmount), selectionType, clickEvent, isSwMoneyBusinessLine);
    }, [clickEvent, data.addMoneyData, navigateTo, selectedAmount, isSwMoneyBusinessLine]);

    const _onBack = useCallback(() => {
        const originalBusinessLine = getOriginalBusinessLine();
        // Skip existing flow and close-webview directly
        if (isSwMoneyBusinessLine) {
            closeWebview();
            return;
        }
        GlobalHeaderState.updateHeaders({
            paymentamount: "",
            marketplacebusinessline: originalBusinessLine,
        });
        setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
    }, [setCheckoutState, isSwMoneyBusinessLine]);

    // flow when user coming from swiggy money ledger page
    const _redirectToVoucherPage = useCallback(() => {
        clickRedeemVoucherSwMoney(clickEvent);
        if (isSwMoneyBusinessLine) {
            closeWebview({ params: { action: ADD_VOUCHER_CLICK } });
        }
    }, [clickEvent, isSwMoneyBusinessLine]);

    useEffect(() => {
        // trigger event only when balance data is loaded and available
        if (eligibleBalance != null) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.ADD_SWIGGY_MONEY_PAGE_VIEW,
            });
            onAddSwiggyMoneyScreenView(String(eligibleBalance), impressionEvent, isSwMoneyBusinessLine);
        }
    }, [data.addMoneyData, eligibleBalance, impressionEvent, isSwMoneyBusinessLine]);

    useEffect(() => {
        const reqId = window.requestAnimationFrame(requestAddMoneyData);
        return (): void => window.cancelAnimationFrame(reqId);
    }, [requestAddMoneyData]);

    useCustomHardwareBack([CheckoutRoute.ADD_SWIGGY_MONEY], _onBack);

    const onClickPredefinedAmount = useCallback(
        (amount: number) => {
            clickPredefinedRechargeAmount(String(eligibleBalance), String(amount), clickEvent, isSwMoneyBusinessLine);
        },
        [clickEvent, eligibleBalance, isSwMoneyBusinessLine],
    );

    const onSwiggyMoneyInfoCtaClick = useCallback(() => {
        clickSwiggyMoneyInfoCta(SCREEN_NAME.SWIGGY_MONEY_TOPUP_PAGE, clickEvent);
    }, [clickEvent]);

    const onClickSwiggyMoneyInfoHalfCardActionButton = useCallback(() => {
        closeSwiggyMoneyInfoHalfCard(
            SCREEN_NAME.SWIGGY_MONEY_TOPUP_PAGE,
            CONTEXT.CLICK_SWIGGY_MONEY_INFO_HALF_CARD_ACTION_BUTTON,
            clickEvent,
        );
    }, [clickEvent]);

    const onClickSwiggyMoneyInfoHalfCardCloseButton = useCallback(() => {
        closeSwiggyMoneyInfoHalfCard(
            SCREEN_NAME.SWIGGY_MONEY_TOPUP_PAGE,
            CONTEXT.CLICK_SWIGGY_MONEY_INFO_HALF_CARD_CLOSE_BUTTON,
            clickEvent,
        );
    }, [clickEvent]);

    const onImpressionSwiggyMoneyInfoHalfCard = useCallback(() => {
        impressionSwiggyMoneyInfoHalfCard(SCREEN_NAME.SWIGGY_MONEY_TOPUP_PAGE, impressionEvent);
    }, [impressionEvent]);

    const onSwiggyMoneyInfoCtaImpression = useCallback(() => {
        impressionSwiggyMoneyInfoCta(SCREEN_NAME.SWIGGY_MONEY_TOPUP_PAGE, impressionEvent);
    }, [impressionEvent]);

    const swiggyMoneyInfoActions = useMemo(
        () => ({
            onSwiggyMoneyInfoCtaClick,
            onSwiggyMoneyInfoCtaImpression,
            onClickSwiggyMoneyInfoHalfCardActionButton,
            onClickSwiggyMoneyInfoHalfCardCloseButton,
            onImpressionSwiggyMoneyInfoHalfCard,
        }),
        [
            onClickSwiggyMoneyInfoHalfCardActionButton,
            onClickSwiggyMoneyInfoHalfCardCloseButton,
            onImpressionSwiggyMoneyInfoHalfCard,
            onSwiggyMoneyInfoCtaClick,
            onSwiggyMoneyInfoCtaImpression,
        ],
    );

    const additionalTopupRequired = getAdditionalRechargeRequired(originalBillAmount, eligibleBalance);
    const subtitle = getAddSwiggyMoneySubtitle(
        additionalTopupRequired,
        eligibleBalance ?? 0,
        data.fetchDataInProgress || !data.addMoneyData,
    );
    const header = <CommonHeaderContent title={TEXTS.ADD_MONEY_PAGE_TITLE} subtitle={subtitle} grayscale />;

    // error state
    if (data.criticalError?.error) {
        return (
            <CommonUIWrapper
                header={header}
                onBack={_onBack}
                content={<GenericError action={{ onClick: requestAddMoneyData }} />}
                containerCSSClass={styles.container}
            />
        );
    }

    // loading state
    if (data.fetchDataInProgress || !data.addMoneyData)
        return (
            <CommonUIWrapper
                header={header}
                content={<SwiggyMoneyLoadingScreen />}
                containerCSSClass={styles.container}
            />
        );

    // success state
    const content = (
        <AddSwiggyMoney
            selectionAmount={selectedAmount}
            offers={data.addMoneyData.offers}
            eligibilityDetails={data.addMoneyData.eligibilityDetails}
            onAddMoney={onAddMoney}
            updateSelectedAmount={updateSelectedAmount}
            balance={data.addMoneyData.eligibleBalance}
            clickPredefinedAmount={onClickPredefinedAmount}
            swiggyMoneyInfoActions={swiggyMoneyInfoActions}
            isSwMoneyBusinessLine={isSwMoneyBusinessLine}
            redirectToVoucher={_redirectToVoucherPage}
        ></AddSwiggyMoney>
    );
    return <CommonUIWrapper onBack={_onBack} header={header} content={content} containerCSSClass={styles.container} />;
});
